/**
 * gameCreator/model.js - the ten phases and fifty-three levels, arranged for
 * the person deciding what a NEW game will cover.
 *
 * WHY THIS MODULE EXISTS
 * The creator page is the only screen in the product that shows the WHOLE
 * framework rather than one child's slice of it, and the one thing it must
 * never do is carry its own copy of it. Everything below is derived at import
 * time from engine/framework.js, so a phase added to the framework appears on
 * the page with no edit here, and a phase that is not in the framework cannot
 * be ticked. A hardcoded list would drift, and the drift would be invisible:
 * the page would keep rendering, and would simply describe a framework that no
 * longer exists.
 *
 * THE PLAIN LANGUAGE IS NOT WRITTEN HERE EITHER.
 * Phases use the framework's own `parentName` and `parentDescription`; levels
 * use its `intent`; the per-box wording is chart/parentLanguage.js's, which
 * already covers all 45 components and is the wording a parent reads on the
 * chart. A second set of words written for this page would say the same things
 * slightly differently, and then two places would have to be kept in step.
 *
 * Pure. No state, no DOM, no network - which is what lets
 * client/test/adminGameCreator.test.js assert the counts against the framework
 * without a browser.
 */

import {
  PHASE_NUMBERS,
  getPhase,
  getBoxesForLevel,
  validateCoverage,
  CAPABILITIES,
  STRICTNESS_BANDS,
  FRAMEWORK_SHAPE,
} from '../../../engine/framework.js';
import { boxLanguage } from '../../../chart/parentLanguage.js';

/* =========================================================================
 * LEVEL KEYS
 * One string per level, because the page holds the tick state in a plain
 * object and a (phase, level) pair is not a key. The separator is ':' and not
 * '.' so a key can never be mistaken for the box-id format, which is a
 * database key and must not leak into UI state.
 * ========================================================================= */

/** @returns {string} e.g. "3:2" */
export function levelKey(phase, level) {
  return `${phase}:${level}`;
}

/** @returns {{phase:number, level:number}|null} */
export function parseLevelKey(key) {
  const [phase, level] = String(key).split(':').map(Number);
  if (!Number.isInteger(phase) || !Number.isInteger(level)) return null;
  return { phase, level };
}

/* =========================================================================
 * THE TREE
 * Built once, frozen. Ten phases, each with its levels, each level with the
 * boxes it measures named in a parent's words.
 * ========================================================================= */

function buildLevel(phase, row) {
  const boxes = getBoxesForLevel(phase, row.level);

  const skills = boxes.map((box) => {
    const words = boxLanguage(phase, box.code, box.name);
    return Object.freeze({
      boxId: box.boxId,
      code: box.code,
      // The framework's name, for the person who knows the framework.
      name: box.name,
      // The parent's name, for the person who does not.
      short: words.short,
      means: words.means,
      role: box.role,
      observational: Boolean(box.observational),
      requires: box.requires || null,
      conditional: box.conditional || null,
    });
  });

  return Object.freeze({
    phase,
    level: row.level,
    key: levelKey(phase, row.level),
    // The framework's own one-line statement of what the level asks for.
    intent: row.intent,
    boxCount: boxes.length,
    skills: Object.freeze(skills),
    // Capabilities without which this level measures less than it looks like
    // it measures. Phase 2 is the reason this is surfaced at all: a game
    // covering it without prompt_sequence records nothing, for ever, silently.
    requires: Object.freeze([...new Set(boxes.map((b) => b.requires).filter(Boolean))]),
    observationalCount: boxes.filter((b) => b.observational).length,
  });
}

export const PHASE_TREE = Object.freeze(PHASE_NUMBERS.map((phase) => {
  const p = getPhase(phase);
  const levels = p.levels.map((row) => buildLevel(phase, row));
  const band = STRICTNESS_BANDS[p.strictness];

  return Object.freeze({
    phase,
    // Three names, because three different readers use this page.
    name: p.name,
    parentName: p.parentName,
    // The line for someone who has never heard of ABA.
    plain: p.parentDescription,
    goal: p.goal,
    childSays: p.childSays,
    mlu: p.mlu,
    promptStyle: p.promptStyle.style,
    promptMustInclude: p.promptStyle.mustInclude,
    strictness: p.strictness,
    strictnessAccepts: band ? band.accepts : '',
    levelCount: levels.length,
    boxCount: levels.reduce((n, l) => n + l.boxCount, 0),
    requires: Object.freeze([...new Set(levels.flatMap((l) => l.requires))]),
    observationalCount: levels.reduce((n, l) => n + l.observationalCount, 0),
    levels: Object.freeze(levels),
  });
}));

/** Every level in chart order, flattened. 53 of them. */
export const ALL_LEVELS = Object.freeze(PHASE_TREE.flatMap((p) => p.levels));

const LEVEL_BY_KEY = Object.freeze(
  ALL_LEVELS.reduce((acc, l) => { acc[l.key] = l; return acc; }, {})
);

/** @returns {object|null} the tree entry for one level. */
export function getLevelEntry(key) {
  return LEVEL_BY_KEY[key] || null;
}

/** The framework's own counts, for the page footer. Never typed out by hand. */
export const SHAPE = FRAMEWORK_SHAPE;

/* =========================================================================
 * A SELECTION
 * The page holds `{ [key]: true }`. Everything below reads that.
 * ========================================================================= */

/** Selected keys in chart order, ignoring anything that is not a real level. */
export function orderedSelection(selected) {
  return ALL_LEVELS.filter((l) => selected && selected[l.key]).map((l) => l.key);
}

/**
 * The selection grouped by phase, in chart order.
 * @returns {Array<{phase:number, name:string, parentName:string, plain:string,
 *                  levelCount:number, levels:Array<object>}>}
 */
export function summariseSelection(selected) {
  return PHASE_TREE
    .map((p) => {
      const levels = p.levels.filter((l) => selected && selected[l.key]);
      if (!levels.length) return null;
      return {
        phase: p.phase,
        name: p.name,
        parentName: p.parentName,
        plain: p.plain,
        promptStyle: p.promptStyle,
        promptMustInclude: p.promptMustInclude,
        strictnessAccepts: p.strictnessAccepts,
        levelCount: levels.length,
        levels,
      };
    })
    .filter(Boolean);
}

/**
 * The capabilities this selection obliges the game to declare AND deliver.
 *
 * Derived from the boxes, never from a list: a box carries `requires`, and a
 * game that does not declare it scores that box never. Contract point 2.
 *
 * @returns {Array<{id:string, name:string, detail:string, boxes:number,
 *                  phases:number[], total:number}>}
 */
export function requiredCapabilities(selected) {
  const found = new Map();

  for (const key of orderedSelection(selected)) {
    const entry = LEVEL_BY_KEY[key];
    for (const skill of entry.skills) {
      if (!skill.requires) continue;
      const row = found.get(skill.requires) || { boxes: 0, phases: new Set() };
      row.boxes += 1;
      row.phases.add(entry.phase);
      found.set(skill.requires, row);
    }
  }

  return [...found.entries()].map(([id, row]) => {
    const cap = CAPABILITIES[id] || { name: id, detail: '', boxes: 0 };
    return {
      id,
      name: cap.name,
      detail: cap.detail,
      // How many of the selected boxes depend on it.
      boxes: row.boxes,
      phases: [...row.phases].sort((a, b) => a - b),
      // How many boxes depend on it framework-wide, for context.
      total: cap.boxes || 0,
    };
  }).sort((a, b) => b.boxes - a.boxes);
}

/** Totals for the selection: levels, boxes, and the boxes nobody can score. */
export function selectionTotals(selected) {
  const keys = orderedSelection(selected);
  let boxes = 0;
  let observational = 0;
  for (const key of keys) {
    boxes += LEVEL_BY_KEY[key].boxCount;
    observational += LEVEL_BY_KEY[key].observationalCount;
  }
  return { levels: keys.length, boxes, observational };
}

/* =========================================================================
 * WHAT IS ALREADY COVERED
 *
 * Read from the games REGISTRY, not from the games' own content modules.
 * Contract point 1: what a build covers is a fact the server holds, and it is
 * the registry that the engine consults when it places a child. Importing the
 * two existing games' content here would also mean this page needed editing
 * every time a game shipped, which is the drift it is supposed to prevent.
 *
 * A game whose declaration does not validate is reported rather than guessed
 * at - a wrong "already covered" badge would send someone off to build a game
 * that duplicates one that exists.
 * ========================================================================= */

/**
 * @param {Array<{id:string, name:string, coverage:object|null}>} games
 * @returns {{byPhase:Object<number,string[]>, byLevel:Object<string,string[]>,
 *            problems:Array<{game:string, errors:string[]}>}}
 */
export function coverageIndex(games) {
  const byPhase = {};
  const byLevel = {};
  const problems = [];

  for (const game of games || []) {
    const result = validateCoverage(game.coverage || null);
    if (!result.ok) {
      problems.push({ game: game.name || game.id, errors: result.errors });
      continue;
    }

    // Nulls mean "no restriction", which is the whole framework - see
    // validateCoverage. A game that declares nothing covers all 53 levels.
    const levels = result.availableLevels
      || ALL_LEVELS
        .filter((l) => !result.availablePhases || result.availablePhases.includes(l.phase))
        .map((l) => ({ phase: l.phase, level: l.level }));

    const label = game.name || game.id;
    for (const { phase, level } of levels) {
      const key = levelKey(phase, level);
      if (!byPhase[phase]) byPhase[phase] = [];
      if (!byPhase[phase].includes(label)) byPhase[phase].push(label);
      if (!byLevel[key]) byLevel[key] = [];
      if (!byLevel[key].includes(label)) byLevel[key].push(label);
    }
  }

  return { byPhase, byLevel, problems };
}

export default {
  PHASE_TREE,
  ALL_LEVELS,
  SHAPE,
  levelKey,
  parseLevelKey,
  getLevelEntry,
  orderedSelection,
  summariseSelection,
  requiredCapabilities,
  selectionTotals,
  coverageIndex,
};
